// src/components/tickets/TicketDetailPage.tsx
//
// The full ticket screen (Story 4.2, FR-23) — everything about a question on one
// screen. This REPLACES Story 4.1's one-card stub IN PLACE at the same route
// (/tickets/:id), so 3.7's assignment-email deep links keep resolving with no
// URL churn. It is a pure composition shell: useTicketDetail owns all reads (via
// ticketsService, AD-3) + the single tx_link_tickets mutation; the panels under
// detail/ render the bundle.
//
// Layout: two-column at ≥ lg — work column (~2/3: transcript, draft, thread),
// context column (~1/3: structured fields, KB sources, assignment history,
// related tickets) — stacking single-column below lg. Realtime refetches update
// in place with a quiet "Updated just now" marker; no loading flash, no focus
// stealing (useTicketDetail keeps them silent).

import { useRef, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useTicketDetail } from '../../hooks/useTicketDetail'
import { useAuthStore } from '../../stores/authStore'
import { TicketHeader } from './detail/TicketHeader'
import { TranscriptViewer } from './detail/TranscriptViewer'
import { DraftPanel } from './detail/DraftPanel'
import { ThreadList } from './detail/ThreadList'
import { Composer, type ComposerHandle } from './detail/Composer'
import { CloseConfirm } from './detail/CloseConfirm'
import { EscalateDialog } from './detail/EscalateDialog'
import { ReassignDialog } from './detail/ReassignDialog'
import { StructuredFieldsPanel } from './detail/StructuredFieldsPanel'
import { KbSourcesCard } from './detail/KbSourcesCard'
import { AssignmentHistory } from './detail/AssignmentHistory'
import { RelatedTicketsCard } from './detail/RelatedTicketsCard'
import { nextLayerFor } from '../../utils/escalation'
import { assigneeName } from '../../utils/ticketDisplay'

// Verbatim FR-16 copy for a disabled Escalate at the terminal layer.
const TERMINAL_COPY =
  'This is the final escalation layer for this question type.'
// Non-assignee escalate copy (the reassign-to-self escape hatch).
const NON_ASSIGNEE_COPY =
  'Only the current assignee can escalate — reassign the ticket to yourself first.'

function DetailSkeleton() {
  return (
    <div className="space-y-4">
      <div className="card animate-pulse space-y-4 p-6">
        <div className="h-4 w-32 rounded bg-gray-200" />
        <div className="h-7 w-2/3 rounded bg-gray-200" />
        <div className="h-4 w-1/2 rounded bg-gray-200" />
      </div>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          <div className="card h-48 animate-pulse bg-gray-50" />
          <div className="card h-40 animate-pulse bg-gray-50" />
        </div>
        <div className="space-y-4 lg:col-span-1">
          <div className="card h-40 animate-pulse bg-gray-50" />
          <div className="card h-32 animate-pulse bg-gray-50" />
        </div>
      </div>
    </div>
  )
}

export function TicketDetailPage() {
  const { id } = useParams<{ id: string }>()
  const {
    detail,
    loading,
    error,
    updatedJustNow,
    responding,
    noting,
    closing,
    escalating,
    reassigning,
    correctingField,
    refetch,
    link,
    respond,
    note,
    close,
    escalate,
    reassign,
    correctField,
  } = useTicketDetail(id)

  const currentUserId = useAuthStore(s => s.user?.id ?? null)
  const composerRef = useRef<ComposerHandle>(null)
  const [closeOpen, setCloseOpen] = useState(false)
  const [escalateOpen, setEscalateOpen] = useState(false)
  const [reassignOpen, setReassignOpen] = useState(false)
  const [escalateError, setEscalateError] = useState<string | null>(null)
  const [reassignError, setReassignError] = useState<string | null>(null)
  // Inline success confirmation ('escalated' | 'reassigned') — rendered from the
  // POST-refetch assignee (no optimistic name).
  const [confirmKind, setConfirmKind] = useState<
    'escalated' | 'reassigned' | null
  >(null)

  if (loading) return <DetailSkeleton />

  if (error) {
    return (
      <div className="alert alert-error flex items-center justify-between">
        <span>{error}</span>
        <button
          type="button"
          onClick={() => refetch()}
          className="text-sm font-medium underline"
        >
          Retry
        </button>
      </div>
    )
  }

  if (!detail) {
    // Not-found — 4.1's neutral copy, unchanged.
    return (
      <div className="space-y-4">
        <div className="card p-6 text-center">
          <p className="text-gray-500">
            This ticket doesn&apos;t exist or you don&apos;t have access to it.
          </p>
          <Link
            to="/"
            className="mt-2 inline-block text-sm text-brand-600 hover:text-brand-700 hover:underline"
          >
            Back to My tickets
          </Link>
        </div>
      </div>
    )
  }

  const { ticket, call } = detail
  const repName = call?.rep?.name ?? null
  const repFirstName = repName ? repName.split(/\s+/)[0] : null
  const isClosed = ticket.status === 'closed'
  const isOpen = ticket.status === 'open'
  const isFirstResponse = detail.messages.every(m => m.kind !== 'response')

  // Escalation state (AC 2). Current layer = the latest assignment row's layer_id
  // — the service returns assignments ordered created_at asc / id asc, so the LAST
  // element IS the latest by 3.5's ordering rule (asc last = desc first).
  const isAssignee =
    currentUserId !== null && ticket.assignee_user_id === currentUserId
  const layers = detail.rule?.layers ?? []
  const currentLayerId =
    detail.assignments.length > 0
      ? (detail.assignments[detail.assignments.length - 1].layer_id ?? null)
      : null
  const nextLayer = nextLayerFor(layers, currentLayerId)
  const isTerminal = nextLayer === null
  // Restart-from-layer-1 (NULL/stale current) drives the dialog's copy.
  const fromStart =
    nextLayer !== null &&
    (currentLayerId === null ||
      !layers.some(l => l.layer_id === currentLayerId))
  const escalateDisabledReason = !isAssignee
    ? NON_ASSIGNEE_COPY
    : isTerminal
      ? TERMINAL_COPY
      : null

  // The header actions — quiet/outline buttons in the reserved slot. Empty when
  // the ticket is closed (the meta line renders under the thread instead).
  const headerActions = isClosed ? null : (
    <div className="flex flex-col items-end gap-1">
      <div className="flex flex-wrap justify-end gap-2">
        <button
          type="button"
          onClick={() => composerRef.current?.activateResponse()}
          className="btn-outline"
        >
          Respond
        </button>
        <button
          type="button"
          onClick={() => composerRef.current?.activateNote()}
          className="btn-outline"
        >
          Internal note
        </button>
        {/* Escalate — OPEN only; disabled+copy for non-assignee and at terminal. */}
        {isOpen ? (
          <button
            type="button"
            onClick={() => {
              setEscalateError(null)
              setConfirmKind(null)
              setEscalateOpen(true)
            }}
            disabled={escalateDisabledReason !== null}
            title={escalateDisabledReason ?? undefined}
            className="btn-outline"
          >
            Escalate
          </button>
        ) : null}
        {/* Reassign — OPEN + ANSWERED, for every MA user. */}
        <button
          type="button"
          onClick={() => {
            setReassignError(null)
            setConfirmKind(null)
            setReassignOpen(true)
          }}
          className="btn-outline"
        >
          Reassign
        </button>
        <button
          type="button"
          onClick={() => setCloseOpen(true)}
          className="btn-outline"
        >
          Close
        </button>
      </div>
      {/* Disabled controls always say why (EXPERIENCE). */}
      {isOpen && escalateDisabledReason ? (
        <p className="max-w-xs text-right text-xs text-gray-500">
          {escalateDisabledReason}
        </p>
      ) : null}
    </div>
  )

  return (
    <div className="space-y-4">
      <TicketHeader detail={detail} actions={headerActions} />

      {confirmKind ? (
        <p className="text-xs text-gray-500" role="status" aria-live="polite">
          {confirmKind === 'escalated'
            ? `Escalated — this ticket is now assigned to ${assigneeName(ticket)}.`
            : `Reassigned to ${assigneeName(ticket)}.`}
        </p>
      ) : updatedJustNow ? (
        <p className="text-xs text-gray-400" role="status" aria-live="polite">
          Updated just now
        </p>
      ) : null}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {/* Work column (~2/3). */}
        <div className="space-y-4 lg:col-span-2">
          <TranscriptViewer
            transcript={call?.transcript ?? null}
            siblings={detail.siblings}
            currentTicketId={ticket.id}
          />
          {ticket.draft !== null ? (
            <DraftPanel
              draft={ticket.draft}
              route={ticket.route_label}
              pins={detail.pins}
              onUseDraft={
                isClosed ? undefined : () => composerRef.current?.useDraft()
              }
            />
          ) : null}
          <ThreadList messages={detail.messages} repName={repName} />
          {isClosed ? (
            <p className="rounded-md bg-gray-50 px-4 py-3 text-sm text-gray-500">
              This ticket is closed. A reply from {repName ?? 'the rep'} reopens
              it.
            </p>
          ) : (
            <Composer
              ref={composerRef}
              draft={ticket.draft}
              repName={repName}
              isFirstResponse={isFirstResponse}
              responding={responding}
              noting={noting}
              onRespond={respond}
              onNote={note}
            />
          )}
        </div>

        {/* Context column (~1/3). */}
        <div className="space-y-4 lg:col-span-1">
          <StructuredFieldsPanel
            detail={detail}
            currentUserId={currentUserId}
            correctingField={correctingField}
            onCorrect={correctField}
            onOpenReassign={() => {
              setReassignError(null)
              setConfirmKind(null)
              setReassignOpen(true)
            }}
          />
          <KbSourcesCard pins={detail.pins} />
          <AssignmentHistory
            assignments={detail.assignments}
            rule={detail.rule}
            messages={detail.messages}
          />
          <RelatedTicketsCard
            ticketId={ticket.id}
            relatedTickets={detail.relatedTickets}
            onLink={link}
          />
        </div>
      </div>

      {closeOpen ? (
        <CloseConfirm
          repFirstName={repFirstName}
          closing={closing}
          onConfirm={async () => {
            try {
              await close()
              setCloseOpen(false)
            } catch {
              // The refetch keeps the screen truthful; the dialog stays open so
              // the user sees the action didn't take. (Mapped copy surfacing is
              // 4.8's failure-view scope; close rarely fails business-side.)
              setCloseOpen(false)
            }
          }}
          onCancel={() => setCloseOpen(false)}
        />
      ) : null}

      {escalateOpen && nextLayer ? (
        <EscalateDialog
          nextLayerPosition={nextLayer.position}
          nextLayerMatchers={nextLayer.match_attributes}
          fromStart={fromStart}
          escalating={escalating}
          error={escalateError}
          onEscalate={async noteText => {
            setEscalateError(null)
            try {
              await escalate(noteText)
              setEscalateOpen(false)
              setConfirmKind('escalated')
            } catch (err) {
              // Keep the dialog open with the typed note preserved (the dialog
              // owns its own note state); show the mapped copy inline.
              setEscalateError(
                err instanceof Error ? err.message : 'Escalation failed.'
              )
            }
          }}
          onCancel={() => setEscalateOpen(false)}
        />
      ) : null}

      {reassignOpen ? (
        <ReassignDialog
          currentAssigneeId={ticket.assignee_user_id}
          reassigning={reassigning}
          error={reassignError}
          onReassign={async targetUserId => {
            setReassignError(null)
            try {
              await reassign(targetUserId)
              setReassignOpen(false)
              setConfirmKind('reassigned')
            } catch (err) {
              // Keep the dialog open with the picked user preserved; inline copy.
              setReassignError(
                err instanceof Error ? err.message : 'Reassignment failed.'
              )
            }
          }}
          onCancel={() => setReassignOpen(false)}
        />
      ) : null}
    </div>
  )
}
